import { useEffect, useMemo, useState } from 'react';

function headerPairsFromHeaders(headers) {
  const pairs = [];
  headers.forEach((value, key) => {
    pairs.push([key, value]);
  });
  return pairs.sort((a, b) => a[0].localeCompare(b[0]));
}

function collectBrowserHints() {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const scr = typeof screen !== 'undefined' ? screen : null;
  const conn = nav && 'connection' in nav ? nav.connection : null;

  return {
    userAgent: nav?.userAgent ?? null,
    language: nav?.language ?? null,
    languages: nav?.languages ? [...nav.languages] : null,
    platform: nav?.platform ?? null,
    hardwareConcurrency: nav?.hardwareConcurrency ?? null,
    deviceMemory: nav?.deviceMemory ?? null,
    cookieEnabled: nav?.cookieEnabled ?? null,
    doNotTrack: nav?.doNotTrack ?? null,
    webdriver: nav?.webdriver ?? null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    screen: scr
      ? {
          width: scr.width,
          height: scr.height,
          availWidth: scr.availWidth,
          availHeight: scr.availHeight,
          colorDepth: scr.colorDepth,
          pixelDepth: scr.pixelDepth,
        }
      : null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    connection: conn
      ? {
          effectiveType: conn.effectiveType,
          downlink: conn.downlink,
          rtt: conn.rtt,
          saveData: conn.saveData,
        }
      : null,
  };
}

function JsonBlock({ title, data }) {
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  return (
    <section className="card mb-6">
      <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
      <pre className="text-xs md:text-sm text-gray-300 overflow-x-auto font-mono whitespace-pre-wrap break-all max-h-[28rem] overflow-y-auto">
        {text}
      </pre>
    </section>
  );
}

function Healthcheck() {
  const [edge, setEdge] = useState(null);
  const [edgeError, setEdgeError] = useState(null);
  const [fetchMeta, setFetchMeta] = useState(null);

  const browserHints = useMemo(() => collectBrowserHints(), []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const res = await fetch('/api/healthcheck', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
        });

        const responseHeaders = headerPairsFromHeaders(res.headers);
        const text = await res.text();
        let parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = { raw: text };
        }

        if (!cancelled) {
          setFetchMeta({
            ok: res.ok,
            status: res.status,
            statusText: res.statusText,
            url: res.url,
            responseHeaders,
          });
          setEdge(parsed);
        }
      } catch (e) {
        if (!cancelled) {
          setEdgeError(e instanceof Error ? e.message : String(e));
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const traceSummary = edge && typeof edge === 'object' && edge.derived ? edge.derived : null;
  const fingerprint = edge && typeof edge === 'object' && edge.fingerprint ? edge.fingerprint : null;

  useEffect(() => {
    document.title = 'Velumcloud Healthcheck';

    const existing = document.querySelector('meta[name="robots"]');
    if (existing) {
      existing.setAttribute('content', 'noindex, nofollow');
      return;
    }

    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-cyber-darker text-gray-200 py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            <span className="gradient-text">Connection healthcheck</span>
          </h1>
          <p className="text-gray-400 max-w-3xl">
            Unlinked diagnostic page. Edge data comes from{' '}
            <code className="text-cyber-blue">GET /api/healthcheck</code> (Cloudflare Worker). Use the
            same path with <code className="text-cyber-blue">curl</code> for a raw JSON view. Browser
            hints are only what JavaScript can read locally; they differ from the HTTP request the edge
            saw.
          </p>
        </header>

        {edgeError && (
          <div className="card border-red-900/60 mb-6 text-red-300">
            <p className="font-semibold mb-1">Could not load edge diagnostics</p>
            <p className="text-sm text-red-200/90">{edgeError}</p>
            <p className="text-sm text-gray-400 mt-2">
              For local Vite, run the auth worker with <code className="text-gray-300">wrangler dev</code>{' '}
              and proxy <code className="text-gray-300">/api/healthcheck</code>, or open this page on the
              deployed site.
            </p>
          </div>
        )}

        {fetchMeta && (
          <JsonBlock
            title="This page’s fetch() to /api/healthcheck (browser-visible response metadata)"
            data={fetchMeta}
          />
        )}

        <JsonBlock title="Browser / environment hints (JS-visible fingerprint surface)" data={browserHints} />

        {traceSummary && (
          <section className="card mb-6">
            <h2 className="text-lg font-semibold text-white mb-3">Trace summary (from edge JSON)</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-mono">
              <div>
                <dt className="text-gray-500 mb-0.5">CF-Connecting-IP</dt>
                <dd className="text-cyber-blue break-all">{traceSummary.cfConnectingIp || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-0.5">X-Forwarded-For (raw)</dt>
                <dd className="text-cyber-blue break-all">{traceSummary.xForwardedForRaw || '—'}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-gray-500 mb-0.5">X-Forwarded-For chain</dt>
                <dd className="text-gray-200 break-all">
                  {traceSummary.xForwardedForChain?.length
                    ? traceSummary.xForwardedForChain.join(' → ')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-0.5">Via</dt>
                <dd className="text-gray-200 break-all">{traceSummary.via || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-0.5">Forwarded</dt>
                <dd className="text-gray-200 break-all">{traceSummary.forwarded || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-0.5">True-Client-IP / X-Real-IP</dt>
                <dd className="text-gray-200 break-all">
                  {[traceSummary.trueClientIp, traceSummary.xRealIp].filter(Boolean).join(' / ') || '—'}
                </dd>
              </div>
            </dl>
          </section>
        )}

        {fingerprint && (
          <section className="card mb-6">
            <h2 className="text-lg font-semibold text-white mb-3">Fingerprint hints (server-observed)</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-mono">
              <div>
                <dt className="text-gray-500 mb-0.5">User-Agent SHA-256</dt>
                <dd className="text-cyber-blue break-all">{fingerprint.userAgentHash || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-0.5">CF colo</dt>
                <dd className="text-gray-200 break-all">{fingerprint.cf?.colo || '—'}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-gray-500 mb-0.5">TLS client hello (best available via Cloudflare)</dt>
                <dd className="text-gray-200 break-all">
                  {fingerprint.cf?.tlsClientHello ? 'present' : '—'}
                </dd>
              </div>
            </dl>
          </section>
        )}

        {edge && <JsonBlock title="Full edge payload (request headers + CF request metadata)" data={edge} />}
      </div>
    </div>
  );
}

export default Healthcheck;
